<template>
	<div class="chat-history" :class="{ 'mobile-visible': showMobile }">
		<div class="history-header">
			<h3>对话历史</h3>
			<button class="new-chat-btn" @click="handleNewChat">
				<svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
				</svg>
				<span class="btn-text">新对话</span>
			</button>
		</div>

		<div class="history-list" ref="historyList">
			<div class="empty-state" v-if="conversations.length === 0">
				<div class="empty-icon">
					<svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" 
							d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z">
						</path>
					</svg>
				</div>
				<p class="empty-text">暂无对话记录</p>
				<p class="empty-hint">开始新对话来创建历史记录</p>
			</div>

			<div v-for="conversation in sortedConversations" 
				:key="conversation.id" 
				class="history-item"
				:class="{ 
					active: conversation.id === activeId,
					'has-hover': !isMobile 
				}" 
				@click="handleSelect(conversation.id)"
				@touchstart="handleTouchStart"
				@touchend="handleTouchEnd"
			>
				<div class="item-content">
					<div class="item-title" :title="conversation.title">
						{{ conversation.title }}
					</div>
					<div class="item-meta">
						<span class="item-time">{{ formatTime(conversation.updatedAt) }}</span>
						<span class="message-count" v-if="getMessageCount(conversation.id) > 0">
							{{ getMessageCount(conversation.id) }}条消息
						</span>
					</div>
				</div>
				<button 
					class="delete-btn" 
					@click.stop="handleDelete(conversation.id)"
					:aria-label="`删除对话：${conversation.title}`"
				>
					<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
					</svg>
				</button>
			</div>
		</div>

		<!-- 底部操作区 -->
		<div class="history-footer" v-if="conversations.length > 0">
			<div class="conversation-stats">
				共 {{ conversations.length }} 个对话
			</div>
		</div>

		<!-- 移动端切换按钮 -->
		<button class="mobile-toggle" @click="toggleMobile" v-if="isMobile">
			<svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
				<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
					:d="showMobile ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'">
				</path>
			</svg>
		</button>
	</div>
</template>

<script>
	export default {
		name: 'ChatHistory',

		props: {
			conversations: {
				type: Array,
				default: () => []
			},
			activeId: {
				type: String,
				default: null
			},
			messageCount: {
				type: Object,
				default: () => ({})
			}
		},

		data() {
			return {
				showMobile: false,
				isMobile: false,
				touchStartTime: 0
			}
		},

		computed: {
			sortedConversations() {
				return [...this.conversations].sort((a, b) =>
					new Date(b.updatedAt) - new Date(a.updatedAt)
				)
			}
		},

		mounted() {
			this.checkMobile()
			window.addEventListener('resize', this.checkMobile)
		},

		beforeDestroy() {
			window.removeEventListener('resize', this.checkMobile)
		},

		methods: {
			checkMobile() {
				this.isMobile = window.innerWidth <= 768
				// 桌面端自动显示
				if (!this.isMobile) {
					this.showMobile = false
				}
			},

			formatTime(timestamp) {
				const date = new Date(timestamp)
				const now = new Date()
				const diff = now - date

				if (diff < 60000) return '刚刚'
				if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
				if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
				if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`

				// 超过一周显示具体日期
				if (date.getFullYear() === now.getFullYear()) {
					return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
				}
				return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'short', day: 'numeric' })
			},

			getMessageCount(conversationId) {
				return this.messageCount[conversationId] || 0
			},

			toggleMobile() {
				this.showMobile = !this.showMobile
			},

			handleNewChat() {
				this.$emit('new')
				// 移动端创建新对话后关闭历史面板
				if (this.isMobile) {
					this.showMobile = false
				}
			},

			handleSelect(id) {
				this.$emit('select', id)
				// 移动端选择对话后关闭历史面板
				if (this.isMobile) {
					this.showMobile = false
				}
			},

			handleDelete(id) {
				// 添加确认提示
				if (confirm('确定要删除这个对话吗？此操作无法撤销。')) {
					this.$emit('delete', id)
				}
			},

			handleTouchStart() {
				this.touchStartTime = Date.now()
			},

			handleTouchEnd() {
				const touchDuration = Date.now() - this.touchStartTime
				// 长按效果（超过500ms）
				if (touchDuration > 500) {
					// 可以在这里添加长按菜单等功能
					console.log('Long press detected')
				}
			}
		}
	}
</script>

<style scoped>
	/* 基础容器 */
	.chat-history {
		/* width: 300px; */
		background: var(--bg-glass);
		backdrop-filter: blur(var(--blur-md));
		border-right: 1px solid var(--border-light);
		display: flex;
		flex-direction: column;
		position: relative;
		height: 100%;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	}

	/* 头部区域 */
	.history-header {
		padding: 20px;
		border-bottom: 1px solid var(--border-light);
		display: flex;
		align-items: center;
		justify-content: space-between;
		background: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(8px);
		flex-shrink: 0;
	}

	.history-header h3 {
		font-size: 18px;
		font-weight: 600;
		color: var(--text-dark);
		margin: 0;
		letter-spacing: -0.02em;
	}

	.new-chat-btn {
		background: var(--primary);
		color: white;
		border: none;
		padding: 10px 16px;
		border-radius: 20px;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		display: flex;
		align-items: center;
		gap: 8px;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		white-space: nowrap;
		box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
	}

	.new-chat-btn:hover {
		background: var(--primary-dark);
		transform: translateY(-1px);
		box-shadow: 0 4px 12px rgba(0, 122, 255, 0.4);
	}

	.new-chat-btn:active {
		transform: translateY(0);
	}

	/* 列表区域 */
	.history-list {
		flex: 1;
		overflow-y: auto;
		padding: 12px;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: thin;
		scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
	}

	.history-list::-webkit-scrollbar {
		width: 6px;
	}

	.history-list::-webkit-scrollbar-track {
		background: transparent;
	}

	.history-list::-webkit-scrollbar-thumb {
		background: rgba(0, 0, 0, 0.2);
		border-radius: 3px;
	}

	.history-list::-webkit-scrollbar-thumb:hover {
		background: rgba(0, 0, 0, 0.3);
	}

	/* 空状态 */
	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 40px 20px;
		text-align: center;
		height: 200px;
	}

	.empty-icon {
		margin-bottom: 16px;
		opacity: 0.5;
		color: var(--text-light);
	}

	.empty-text {
		font-size: 16px;
		font-weight: 500;
		color: var(--text-dark);
		margin: 0 0 8px 0;
	}

	.empty-hint {
		font-size: 14px;
		color: var(--text-light);
		margin: 0;
	}

	/* 历史项目 */
	.history-item {
		background: rgba(255, 255, 255, 0.8);
		border-radius: 12px;
		padding: 12px 16px;
		margin-bottom: 8px;
		cursor: pointer;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		border: 1px solid transparent;
		position: relative;
		overflow: hidden;
	}

	.history-item.has-hover:hover {
		transform: translateY(-1px);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.history-item.active {
		background: var(--primary);
		color: white;
		box-shadow: 0 2px 12px rgba(0, 122, 255, 0.3);
	}

	.history-item.active .item-time,
	.history-item.active .message-count {
		color: rgba(255, 255, 255, 0.8);
	}

	.item-content {
		flex: 1;
		min-width: 0;
	}

	.item-title {
		font-size: 15px;
		font-weight: 500;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		line-height: 1.4;
		margin-bottom: 4px;
		color: inherit;
	}

	.item-meta {
		display: flex;
		align-items: center;
		gap: 12px;
		font-size: 12px;
	}

	.item-time {
		color: var(--text-light);
		font-size: 12px;
	}

	.history-item.active .item-time {
		color: rgba(255, 255, 255, 0.7);
	}

	.message-count {
		display: none; /* 隐藏消息计数，保持简洁 */
	}

	.delete-btn {
		width: 32px;
		height: 32px;
		border-radius: 50%;
		background: transparent;
		border: none;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 0;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		flex-shrink: 0;
		color: var(--text-light);
	}

	.history-item:hover .delete-btn {
		opacity: 1;
	}

	.delete-btn:hover {
		background: rgba(255, 59, 48, 0.1);
		color: var(--danger);
		transform: scale(1.1);
	}

	.delete-btn:active {
		transform: scale(0.95);
	}

	/* 底部区域 */
	.history-footer {
		padding: 16px 20px;
		border-top: 1px solid var(--border-light);
		background: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(8px);
		flex-shrink: 0;
	}

	.conversation-stats {
		font-size: 13px;
		color: var(--text-light);
		text-align: center;
	}

	/* 移动端切换按钮 */
	.mobile-toggle {
		display: none;
		position: absolute;
		top: 20px;
		right: -50px;
		width: 44px;
		height: 44px;
		background: var(--bg-glass);
		backdrop-filter: blur(var(--blur-md));
		border: 1px solid var(--border-light);
		border-radius: 0 12px 12px 0;
		cursor: pointer;
		align-items: center;
		justify-content: center;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		color: var(--primary);
		z-index: 1000;
	}

	.mobile-toggle:hover {
		background: rgba(255, 255, 255, 1);
		transform: translateX(-2px);
	}

	.mobile-toggle:active {
		transform: translateX(0) scale(0.95);
	}

	/* 平板设备优化 (768px - 1024px) */
	@media (max-width: 1024px) and (min-width: 769px) {
		.chat-history {
			width: 280px;
		}

		.history-header {
			padding: 16px 18px;
		}

		.history-header h3 {
			font-size: 17px;
		}

		.new-chat-btn {
			padding: 8px 14px;
			font-size: 13px;
		}
	}

	/* 移动端优化 (≤768px) */
	@media (max-width: 768px) {
		.chat-history {
			position: fixed;
			left: 0;
			top: 0;
			bottom: 0;
			width: 320px;
			max-width: 85vw;
			z-index: 1000;
			transform: translateX(-100%);
			transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
			box-shadow: none;
		}

		.chat-history.mobile-visible {
			transform: translateX(0);
			box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
		}

		.mobile-toggle {
			display: flex;
		}

		.history-header {
			padding: 16px;
		}

		.history-header h3 {
			font-size: 16px;
		}

		.new-chat-btn {
			padding: 8px 12px;
			font-size: 13px;
		}

		.btn-text {
			display: none;
		}

		.history-list {
			padding: 8px 12px;
		}

		.history-item {
			padding: 12px 14px;
			margin-bottom: 6px;
		}

		.history-item.has-hover:hover {
			transform: none;
		}

		.history-item:active {
			background: rgba(0, 122, 255, 0.1);
			transform: scale(0.98);
		}

		.delete-btn {
			opacity: 1;
			width: 36px;
			height: 36px;
		}

		.history-footer {
			padding: 12px 16px;
		}
	}

	/* 小屏手机优化 (≤480px) */
	@media (max-width: 480px) {
		.chat-history {
			width: 280px;
			max-width: 90vw;
		}

		.history-header {
			padding: 12px 14px;
		}

		.history-header h3 {
			font-size: 15px;
		}

		.new-chat-btn {
			padding: 6px 10px;
			font-size: 12px;
		}

		.new-chat-btn svg {
			width: 16px;
			height: 16px;
		}

		.mobile-toggle {
			width: 40px;
			height: 40px;
			right: -45px;
		}

		.mobile-toggle svg {
			width: 20px;
			height: 20px;
		}

		.history-item {
			padding: 10px 12px;
		}

		.item-title {
			font-size: 14px;
		}

		.item-meta {
			font-size: 11px;
		}

		.delete-btn {
			width: 32px;
			height: 32px;
		}

		.delete-btn svg {
			width: 14px;
			height: 14px;
		}
	}

	/* 超小屏优化 (≤360px) */
	@media (max-width: 360px) {
		.chat-history {
			width: 260px;
			max-width: 95vw;
		}

		.history-header {
			padding: 10px 12px;
		}

		.history-header h3 {
			font-size: 14px;
		}
	}

	/* 横屏模式优化 */
	@media (max-height: 500px) and (orientation: landscape) {
		.history-header {
			padding: 12px 16px;
		}

		.history-header h3 {
			font-size: 15px;
		}

		.history-list {
			padding: 8px 12px;
		}

		.history-item {
			padding: 8px 12px;
			margin-bottom: 4px;
		}

		.history-footer {
			padding: 8px 16px;
		}

		.empty-state {
			height: 150px;
			padding: 20px;
		}

		.mobile-toggle {
			top: 12px;
			width: 36px;
			height: 36px;
		}
	}

	/* 高DPI屏幕优化 */
	@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
		.history-item {
			border-width: 0.5px;
		}

		.new-chat-btn {
			box-shadow: 0 1px 4px rgba(0, 122, 255, 0.3);
		}
	}

	/* 触摸设备优化 */
	@media (hover: none) and (pointer: coarse) {
		.history-item.has-hover:hover {
			transform: none;
		}

		.new-chat-btn:hover {
			transform: none;
			background: var(--primary);
		}

		.delete-btn:hover {
			background: transparent;
			color: var(--text-light);
		}

		.mobile-toggle:hover {
			transform: none;
			background: var(--bg-glass);
		}

		/* 触摸反馈 */
		.history-item:active {
			background: rgba(0, 122, 255, 0.1);
			transform: scale(0.98);
		}

		.new-chat-btn:active {
			background: var(--primary-dark);
			transform: scale(0.95);
		}

		.delete-btn:active {
			background: rgba(255, 59, 48, 0.1);
			color: var(--danger);
			transform: scale(0.9);
		}
	}

	/* 无障碍优化 */
	@media (prefers-reduced-motion: reduce) {
		.chat-history,
		.history-item,
		.new-chat-btn,
		.delete-btn,
		.mobile-toggle {
			transition: none;
		}

		.history-item.has-hover:hover {
			transform: none;
		}

		.new-chat-btn:hover {
			transform: none;
		}
	}

	/* 深色模式支持 */
	@media (prefers-color-scheme: dark) {
		:root {
			--bg-glass: rgba(45, 55, 72, 0.9);
			--border-light: rgba(255, 255, 255, 0.1);
			--text-dark: #f7fafc;
			--text-light: #a0aec0;
		}

		.chat-history {
			background: var(--bg-glass);
		}

		.history-header {
			background: rgba(45, 55, 72, 0.8);
		}

		.history-item {
			background: rgba(74, 85, 104, 0.8);
		}

		.history-item.has-hover:hover {
			transform: translateY(-1px);
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
		}

		.message-count {
			background: rgba(255, 255, 255, 0.1);
		}

		.history-footer {
			background: rgba(45, 55, 72, 0.8);
		}

		.mobile-toggle {
			background: rgba(45, 55, 72, 0.9);
		}
	}

	/* 打印样式 */
	@media print {
		.chat-history {
			position: static;
			width: 100%;
			background: white;
			backdrop-filter: none;
			border: none;
		}

		.mobile-toggle,
		.new-chat-btn,
		.delete-btn {
			display: none;
		}

		.history-item {
			background: white;
			border: 1px solid #ddd;
			break-inside: avoid;
		}
	}
</style>